/**
 * Money formatter utility for consistent currency formatting across the application
 */

export interface MoneyFormatterOptions {
  currency?: string;
  locale?: string;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
  showCurrency?: boolean;
}

/**
 * Formats a number as currency with proper comma separation
 * @param amount - The amount to format
 * @param options - Formatting options
 * @returns Formatted currency string
 */
export const formatMoney = (
  amount: number | string | null | undefined,
  options: MoneyFormatterOptions = {}
): string => {
  const {
    currency = "NGN",
    locale = "en-NG",
    minimumFractionDigits = 0,
    maximumFractionDigits = 2,
    showCurrency = true,
  } = options;

  // Handle null, undefined, or empty values
  if (amount === null || amount === undefined || amount === "") {
    return showCurrency ? `${currency} 0` : "0";
  }

  // Convert to number if it's a string
  const numericAmount =
    typeof amount === "string" ? parseFloat(amount) : amount;

  // Handle invalid numbers
  if (isNaN(numericAmount)) {
    return showCurrency ? `${currency} 0` : "0";
  }

  // Format the number with locale-specific formatting
  const formattedNumber = numericAmount.toLocaleString(locale, {
    minimumFractionDigits,
    maximumFractionDigits,
  });

  return showCurrency ? `${currency} ${formattedNumber}` : formattedNumber;
};

/**
 * Formats a number as Nigerian Naira with comma separation
 * @param amount - The amount to format
 * @param showCurrency - Whether to show the currency symbol
 * @returns Formatted NGN string
 */
export const formatNGN = (
  amount: number | string | null | undefined,
  showCurrency: boolean = true
): string => {
  return formatMoney(amount, {
    currency: "NGN",
    locale: "en-NG",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
    showCurrency,
  });
};

/**
 * Formats a number as currency with no decimal places
 * @param amount - The amount to format
 * @param currency - The currency code
 * @returns Formatted currency string with no decimals
 */
export const formatMoneyNoDecimals = (
  amount: number | string | null | undefined,
  currency: string = "NGN"
): string => {
  return formatMoney(amount, {
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    showCurrency: true,
  });
};

/**
 * Formats a number as currency with 2 decimal places
 * @param amount - The amount to format
 * @param currency - The currency code
 * @returns Formatted currency string with 2 decimals
 */
export const formatMoneyWithDecimals = (
  amount: number | string | null | undefined,
  currency: string = "NGN"
): string => {
  return formatMoney(amount, {
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    showCurrency: true,
  });
};

/**
 * Parses a formatted currency string back to a number
 * @param formattedAmount - The formatted currency string
 * @returns The numeric value
 */
export const parseMoney = (formattedAmount: string): number => {
  if (!formattedAmount) return 0;

  // Remove currency symbols and commas, then parse
  const cleanAmount = formattedAmount
    .replace(/[^\d.-]/g, "") // Remove everything except digits, dots, and hyphens
    .replace(/,/g, ""); // Remove commas

  const parsed = parseFloat(cleanAmount);
  return isNaN(parsed) ? 0 : parsed;
};
